import { useMutation } from '@tanstack/react-query';
import { useAccountStore } from '@/store/use-account-store';
import { privateApi } from '@/lib/api-clients/rest-client';

type ListenKeyResponse = {
  listenKey: string;
};

const onSuccess = (address: string, res: ListenKeyResponse) => {
  const { getAuthParams, setAuthParams, getAccurateTime } =
    useAccountStore.getState();

  const authParams = getAuthParams(address, { skipFallback: true });

  if (!authParams) {
    console.error('No auth params found');
    return;
  }

  setAuthParams(address, {
    ...authParams,
    listenKey: res.listenKey,
    listenKeyExpiry: getAccurateTime() + 1000 * 60 * 59, // 59 minutes, server by default is 1 hour
  });
};

export function useListenKey() {
  return useMutation({
    mutationFn: async (
      targetAddress?: string,
    ): Promise<ListenKeyResponse> => {
      const { address, getAuthParams } = useAccountStore.getState();
      const resolvedAddress = targetAddress ?? address;

      if (!resolvedAddress) {
        console.error('No address for listen key request');
        return { listenKey: '' };
      }

      const authParams = getAuthParams(resolvedAddress, { skipFallback: true });

      if (!authParams?.apiKey) {
        console.error('No API key for listen key request');
        return { listenKey: '' };
      }

      return privateApi.post(
        '/account/listenKey',
        {},
        {
          headers: {
            'X-API-KEY': authParams.apiKey,
          },
        },
      );
    },
    onSuccess: (res, targetAddress) => {
      const { address } = useAccountStore.getState();
      const resolvedAddress = targetAddress ?? address;

      if (resolvedAddress && res?.listenKey) {
        onSuccess(resolvedAddress, res);
      }
    },
  });
}

export function useRefreshListenKey() {
  return useMutation({
    mutationFn: async (
      targetAddress?: string,
    ): Promise<ListenKeyResponse> => {
      const { address, getAuthParams } = useAccountStore.getState();
      const resolvedAddress = targetAddress ?? address;

      if (!resolvedAddress) {
        console.error('No address for refresh listen key request');
        return { listenKey: '' };
      }

      const authParams = getAuthParams(resolvedAddress, { skipFallback: true });

      if (!authParams?.apiKey) {
        console.error('No API key for refresh listen key request');
        return { listenKey: '' };
      }

      return privateApi.put(
        '/account/listenKey',
        {},
        {
          headers: {
            'X-API-KEY': authParams.apiKey,
          },
        },
      );
    },
    onSuccess: (res, targetAddress) => {
      const { address } = useAccountStore.getState();
      const resolvedAddress = targetAddress ?? address;

      if (resolvedAddress && res?.listenKey) {
        onSuccess(resolvedAddress, res);
      }
    },
  });
}
